import { flatMapSpread, fromCondition, succeed } from "@otto-ec/assets-core-utils/nano-effect";
import type { AgentConfigOptions } from "@elastic/apm-rum";
import type { Effect } from "@otto-ec/assets-core-utils/nano-effect";
import { toggle } from "../../../exports/toggle";
import { getPagecluster } from "../../../exports/misc";
import { random } from "../../../common/tools";
import { apmAsyncScope, noops, transactionTypes } from "./utils";
import { parseTraceparentFlags } from "./traceparent";
import { getSamplingRate, sampligRates } from "./sampleRate";

const log = apmAsyncScope.scope("config");

/**
 *
 *
 */
export const TOGGLE_AGENT_DISABLE = "gr_apm_agent_disable";
/**
 *
 *
 */
export const TOGGLE_DROP_UNSAMPLED_DISABLE = "gr_apm_drop_unsampled_disable";

/**
 *
 *
 */
export const TOGGLE_ENRICH_ERRORS_DISABLE = "gr_apm_enrich_errors_disable";

/**
 *
 *
 */
export const TOGGLE_ADD_ONEX_DATA_TO_APM_CONTEXT = "gr_apm_add_onex_data_to_apm_context";

/**
 *
 *
 *
 */
export const TOGGLE_ENRICH_SPAN_DATA = "gr_apm_enrich_span_data";

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export type InitConfig = [
  config: AgentConfigOptions,
  labels: Labels,
  pageLoadSamplingActive: boolean,
  isToggleEnabled: boolean,
  isAtOttoDe: boolean,
];

/**
 *
 *
 */
export const computeApmActivationRequirements = flatMapSpread(((
  config,
  labels,
  pageLoadActive,
  isToggleActive,
  isAtOttoDe,
) =>
  fromCondition(
    isToggleActive && isAtOttoDe,
    () => (
      log.info("APM Agent active", { config, labels }),
      [config, labels, pageLoadActive] as const
    ),
    () => (log.info("APM Agent inactive", { pageLoadActive, isToggleActive, isAtOttoDe }), noops),
  )) satisfies (...a: InitConfig) => unknown);

/**
 *
 */
export const getApmInitializationConfiguration = (
  rate = random(),
  pc = getPagecluster(),
  host = location.hostname,
): Effect<InitConfig, never> => {
  /*                                                                                  */
  const envRates = sampligRates[import.meta.env.VITE_OTTO_INSTALLATION_ENV];
  if (!envRates) {
    log.warn(
      `Invalid environment configuration '${
        import.meta.env.VITE_OTTO_INSTALLATION_ENV
      }', disable APM agent`,
    );
    return succeed([{}, {}, false, false, false]);
  }

  /*                                         */
  const pagecluster = pc ?? "unknown";
  /*                                                      */
  const samplingRatePageload = getSamplingRate(transactionTypes[0], pagecluster);
  const samplingRateRequests = getSamplingRate(transactionTypes[1], pagecluster);

  const { flags, labels: serverLabels = {}, ...serverConfig } = { ...window.__APM__ };
  log.debug("Received server APM configuration", { serverConfig, serverLabels });

  /*                                 */
  const traceFlags = parseTraceparentFlags(flags);
  log.debug("Parsed traceparent flags", { flags, traceFlags });

  /*                                                                 */
  const labels = {
    sample_rate_active: samplingRatePageload,
    sample_rate_requests: samplingRateRequests,
    pagecluster,
    ...serverLabels,
  };

  /*                                     */
  /*                                                                          */
  /*                                                         */
  /*                                            */
  const pageLoadSamplingActive = !!traceFlags[7] || rate <= samplingRatePageload;

  const agentConfig: AgentConfigOptions = {
    serviceName: import.meta.env.VITE_OTTO_INSTALLATION_NAME,
    serverUrl: import.meta.env.VITE_ELASTIC_APM_SERVER_URL,
    environment: import.meta.env.VITE_OTTO_INSTALLATION_ENV,
    /*                                        */
    /*                                             */
    pageLoadTransactionName: pc ?? undefined,

    /*                                                 */
    /*                                                                         */
    transactionSampleRate: samplingRateRequests,

    /*                                                                          */
    pageLoadSampled: true,

    /*                                                      */
    /*                                                                                 */
    disableInstrumentations: pageLoadSamplingActive
      ? []
      : ["click", "eventtarget", "fetch", "history", "xmlhttprequest", "error"],

    /*                                                           */
    distributedTracing: true,
    /*                                          */
    distributedTracingOrigins: [/(www|develop|api)\.otto\.de/],

    /*                                                     */
    ...serverConfig,
  };

  /*                                         */
  /*                                                            */
  /*                                                          */
  const activationFlags = serverConfig.active
    ? ([true, true, true] as const)
    : ([
        pageLoadSamplingActive,
        /*                                             */
        !toggle.get(TOGGLE_AGENT_DISABLE, false),
        /*                             */
        host.endsWith("otto.de"),
      ] as const);

  return succeed([agentConfig, labels, ...activationFlags]);
};
